import React from 'react';
import { Stage } from '../../types/workflow.types';
import PortfolioSelector from '../PortfolioSelector';

/**
 * Props interface for the ChatbotHeader component
 */
interface ChatbotHeaderProps {
  // Workflow state
  stage: Stage;
  
  // Organization data
  availableOrganizations: any[];
  selectedPortfolio: string;
  onPortfolioChange: (portfolioId: string) => void;
  organizationsLoading: boolean;
  
  // Loading state
  isLoadingState: boolean;
  getLoadingMessage: () => string;
  
  // User information
  userAlias?: string;
  
  // Navigation callbacks
  onBackToDashboard?: () => void;
  onTasksView?: () => void;
  onToggleView?: () => void;
  onResetChat: () => void;
  onHome?: () => void;
  orgId?: string;
}

/**
 * ChatbotHeader Component
 * 
 * Displays the header section of the chatbot interface including:
 * - Title and current stage indicator
 * - Portfolio selector in the center
 * - User display and navigation buttons on the right
 * - Status indicator showing loading state
 * 
 * This component extracts the header logic from the main FillyOnIris component
 * to improve maintainability and separation of concerns.
 */
export const ChatbotHeader: React.FC<ChatbotHeaderProps> = ({
  stage,
  availableOrganizations,
  selectedPortfolio,
  onPortfolioChange,
  organizationsLoading,
  isLoadingState,
  getLoadingMessage,
  userAlias,
  onBackToDashboard,
  onTasksView,
  onToggleView,
  onResetChat,
  onHome,
  orgId
}) => {
  /**
   * Converts the stage enum to a user-friendly display string
   */
  const getStageDisplayName = (currentStage: Stage): string => {
    switch (currentStage) {
      case Stage.RequestType:
        return 'Request Type';
      case Stage.Intake:
        return 'Gathering Info';
      case Stage.Summarization:
        return 'Review';
      case Stage.Complete:
        return 'Complete';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="chatbot-header">
      <div className="chatbot-header-content">
        {/* Left Section - Title and Stage */}
        <div className="chatbot-header-left">
          <div className="chatbot-header-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
              />
            </svg>
          </div>
          <div>
            <h1 className="chatbot-title">Hey Filly!</h1>
            <p className="chatbot-stage">
              Stage: {getStageDisplayName(stage)}
            </p>
          </div>
        </div>
        
        {/* Center Section - intentionally empty (portfolio moved to chat flow) */}
        <div className="chatbot-header-center"></div>

        {/* Right Section - Status, User, and Navigation */}
        <div className="chatbot-header-right">
          {/* Status Indicator */}
          <div className="chatbot-status">
            <div className="chatbot-status-dot"></div>
            <span className="chatbot-status-text">
              {isLoadingState ? getLoadingMessage() : 'Ready'}
            </span>
          </div>
          
          {/* User Display */}
          <div className="chatbot-user-display" style={{
            marginRight: '12px',
            padding: '4px 8px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            fontSize: '14px',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            <span>{userAlias || 'User'}</span>
          </div>

          {/* Navigation Buttons */}
          {/* Home Button — back to org selector */}
          {onHome && (
            <button 
              onClick={onHome} 
              className="chatbot-new-request-btn" 
              style={{marginRight: '8px'}}
              title="Back to Organizations"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l-7-7 7-7"/>
              </svg>
              <span>Home</span>
            </button>
          )}

          {/* Support Icon */}
          <a 
            href="https://form.asana.com/?k=COodoRCQXmFrpLIANjTfiQ&d=8442528107068"
            target="_blank"
            rel="noopener noreferrer"
            className="chatbot-new-request-btn" 
            style={{marginRight: '8px', textDecoration: 'none'}}
            title="Get Support"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <span>Support</span>
          </a>
          
          {onBackToDashboard && (
            <button 
              onClick={onBackToDashboard} 
              className="chatbot-new-request-btn" 
              style={{marginRight: '8px'}}
              title="Go to Filly Hub"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                />
              </svg>
              <span>Filly Hub</span>
            </button>
          )}
          
          {/* {onTasksView && (
            <button 
              onClick={onTasksView} 
              className="chatbot-new-request-btn" 
              style={{marginRight: '8px'}}
              title="Go to Task Prioritization"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" 
                />
              </svg>
              <span>Prioritize</span>
            </button>
          )} */}
          
          {/* {onToggleView && (
            <button 
              onClick={onToggleView} 
              className="chatbot-new-request-btn" 
              style={{marginRight: '8px'}}
              title="Go to Organization Manager"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" 
                />
              </svg>
              <span>Organization Manager</span>
            </button>
          )} */}
          
          {/* New Chat Button */}
          <button 
            onClick={onResetChat} 
            className="chatbot-new-request-btn"
            title="Start a new conversation"
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 4v16m8-8H4" 
              />
            </svg>
            <span>New Chat</span>
          </button>
        </div>
      </div>

      {/* Mode Description Banner */}
      <div className="chatbot-mode-banner">
        <p className="chatbot-mode-description">
          📝 Submit detailed feature requests with guided assistance
        </p>
      </div>
    </div>
  );
};

export default ChatbotHeader;
